import React, { useState } from "react";
import { Lock, Save, User } from "lucide-react";
import { useCustomerAuth } from "@/contexts/CustomerAuthContext";
import { toast } from "sonner";

export default function AccountSecurity() {
  const { customer, updateProfile, changePassword } = useCustomerAuth();
  const [profile, setProfile] = useState({
    name: customer?.name || "",
    phone: customer?.phone || "",
    marketing_consent: !!customer?.marketing_consent,
  });
  const [savingProfile, setSavingProfile] = useState(false);

  const [pwd, setPwd] = useState({ current: "", next: "", confirm: "" });
  const [pwdBusy, setPwdBusy] = useState(false);
  const [pwdError, setPwdError] = useState("");

  const saveProfile = async (e) => {
    e?.preventDefault();
    setSavingProfile(true);
    const res = await updateProfile(profile);
    setSavingProfile(false);
    if (res.ok) toast.success("Profile updated");
    else toast.error(res.error || "Couldn't save");
  };

  const submitPassword = async (e) => {
    e.preventDefault();
    setPwdError("");
    if (pwd.next.length < 6) return setPwdError("New password must be at least 6 characters");
    if (pwd.next !== pwd.confirm) return setPwdError("Passwords do not match");
    setPwdBusy(true);
    const res = await changePassword({ current_password: pwd.current, new_password: pwd.next });
    setPwdBusy(false);
    if (res.ok) {
      toast.success("Password changed");
      setPwd({ current: "", next: "", confirm: "" });
    } else {
      setPwdError(res.error || "Couldn't change password");
    }
  };

  return (
    <div className="space-y-10" data-testid="account-security-page">
      {/* Profile */}
      <section>
        <div className="mb-4">
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#6B5158]">Profile</div>
          <h2 className="font-display text-3xl">Personal details</h2>
        </div>
        <form onSubmit={saveProfile} className="rounded-md border border-[#E6D4CC] bg-white p-5">
          <div className="grid gap-3 md:grid-cols-2">
            <Field icon={User} label="Full name" value={profile.name} onChange={(v) => setProfile({ ...profile, name: v })} testId="sec-name" />
            <Field label="Phone" value={profile.phone} onChange={(v) => setProfile({ ...profile, phone: v })} testId="sec-phone" />
          </div>
          <label className="mt-4 flex items-start gap-2 text-xs text-[#6B5158]">
            <input
              type="checkbox"
              checked={profile.marketing_consent}
              onChange={(e) => setProfile({ ...profile, marketing_consent: e.target.checked })}
              className="mt-0.5 h-4 w-4 accent-[#9D5C6C]"
              data-testid="sec-marketing"
            />
            <span>I'd like occasional product news. (You can unsubscribe anytime.)</span>
          </label>
          <div className="mt-4">
            <button
              type="submit"
              disabled={savingProfile}
              className="inline-flex items-center gap-1.5 rounded-full bg-[#2B2127] px-5 py-2.5 text-xs uppercase tracking-[0.15em] text-white transition-colors hover:bg-[#9D5C6C] disabled:opacity-60"
              data-testid="sec-profile-save"
            >
              <Save size={12} /> {savingProfile ? "Saving…" : "Save changes"}
            </button>
          </div>
        </form>
      </section>

      {/* Change password */}
      <section>
        <div className="mb-4">
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#6B5158]">Security</div>
          <h2 className="font-display text-3xl">Change password</h2>
        </div>
        <form onSubmit={submitPassword} className="rounded-md border border-[#E6D4CC] bg-white p-5">
          <div className="grid gap-3 md:grid-cols-3">
            <Field icon={Lock} type="password" label="Current password" value={pwd.current} onChange={(v) => setPwd({ ...pwd, current: v })} testId="sec-pwd-current" autoComplete="current-password" />
            <Field icon={Lock} type="password" label="New password" value={pwd.next} onChange={(v) => setPwd({ ...pwd, next: v })} testId="sec-pwd-next" autoComplete="new-password" />
            <Field icon={Lock} type="password" label="Confirm new" value={pwd.confirm} onChange={(v) => setPwd({ ...pwd, confirm: v })} testId="sec-pwd-confirm" autoComplete="new-password" />
          </div>
          {pwdError && <div className="mt-3 rounded-md bg-rose-50 p-2 text-xs text-rose-700" data-testid="sec-pwd-error">{pwdError}</div>}
          <div className="mt-4">
            <button
              type="submit"
              disabled={pwdBusy}
              className="inline-flex items-center gap-1.5 rounded-full border border-[#2B2127] bg-white px-5 py-2.5 text-xs uppercase tracking-[0.15em] text-[#2B2127] transition-colors hover:bg-[#2B2127] hover:text-white disabled:opacity-60"
              data-testid="sec-pwd-submit"
            >
              {pwdBusy ? "Updating…" : "Update password"}
            </button>
          </div>
        </form>
      </section>
    </div>
  );
}

function Field({ icon: Icon, label, type = "text", value, onChange, testId, autoComplete }) {
  return (
    <label className="block">
      <span className="mb-1 block text-[10px] uppercase tracking-[0.2em] text-[#6B5158]">{label}</span>
      <span className="relative block">
        {Icon && <Icon size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[#6B5158]" />}
        <input
          type={type}
          autoComplete={autoComplete}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          className={`w-full rounded-md border border-[#E6D4CC] bg-white py-2 pr-3 text-sm outline-none focus:border-[#2B2127] ${Icon ? "pl-9" : "pl-3"}`}
          data-testid={testId}
        />
      </span>
    </label>
  );
}
